/**
 * Cuánto hay de cada producto, en la esquina de su tarjeta de Vender.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE PIDIÓ EL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   «en el cuadro de los items, en la parte inferior derecha muestre cuanto
 *    hay en stock»
 *
 * El número llega con el catálogo (`con_existencia`, ver `useDatosPos`), del
 * servidor y de la sucursal donde se vende. Aquí se decide cómo se dice y de
 * qué color, y se le resta lo que se va vendiendo para que no se quede viejo.
 *
 * Va aparte de `PosPage.tsx` para poder probarlo.
 */

import { formatQty } from "@/lib/money";
import type { ItemListRow } from "@/lib/types";
import { etiquetaDeUnidad } from "./pesoYDinero";
import { conMerma } from "@/lib/merma";

/**
 * De qué color va la esquina.
 *
 *   · `hay`        → gris, sin llamar la atención: es lo normal.
 *   · `pocos`      → ámbar: llegó al mínimo que le puso el dueño.
 *   · `no-alcanza` → rojo: en la cuenta hay MÁS de lo que queda.
 *   · `agotado`    → rojo, y dice «Agotado» en vez de un cero.
 */
export type EstadoDeExistencia = "hay" | "pocos" | "no-alcanza" | "agotado";

type ConExistencia = Pick<ItemListRow, "existencia" | "existencia_minima">;

/**
 * El estado de la esquina, o `null` si no lleva número (un servicio, un
 * combo, algo sin control de inventario, o quien no puede ver existencias).
 *
 * ⚠️ «Pocos» es la misma regla que el inventario (`_estado_de_stock` en
 * `inventory/service.py`): llegar al mínimo, no pasarlo. Si la tarjeta y la
 * pantalla de Inventario no coincidieran, el dueño vería «pocos» en un sitio
 * y «disponible» en el otro para el mismo producto.
 */
export function estadoDeExistencia(
  item: ConExistencia,
  enLaCuenta = 0,
): EstadoDeExistencia | null {
  if (item.existencia == null) return null;
  const hay = Number(item.existencia);
  if (!Number.isFinite(hay)) return null;

  if (hay <= 0) return "agotado";
  // ⚠️ Más de lo que hay, no «igual». Llevarse los tres últimos está bien:
  // es vender lo que queda. Seis de cuatro es lo que el cajero tiene que ver
  // ANTES de cobrar — con el stock negativo apagado (lo de fábrica), el cobro
  // no pasaría y se enteraría con el cliente delante.
  if (enLaCuenta > hay) return "no-alcanza";

  // Un mínimo en cero no avisa nunca: aquí ya se sabe que hay más de cero.
  const minimo = item.existencia_minima == null ? null : Number(item.existencia_minima);
  if (minimo !== null && hay <= minimo) return "pocos";
  return "hay";
}

/**
 * Si está agotado, para «Ocultar los agotados en Vender» (2026-10-07).
 *
 * Con el número (lo de esta sucursal, ya restado lo que se vendió aquí) o, a
 * quien no puede ver cuánto hay, con el sí o el no del servidor. Un servicio
 * o algo sin control de inventario no está agotado nunca.
 */
export function estaAgotado(item: ConExistencia & Pick<ItemListRow, "sin_existencia">): boolean {
  if (item.existencia != null) {
    const hay = Number(item.existencia);
    return Number.isFinite(hay) && hay <= 0;
  }
  return item.sin_existencia === true;
}

/** «12», «1,5 kg». Lo de piezas sin unidad: «12 unidades» es ruido. */
export function cuantoHay(item: ConExistencia & Pick<ItemListRow, "unit">): string {
  const numero = formatQty(Number(item.existencia ?? 0));
  const unidad = etiquetaDeUnidad(item.unit);
  return unidad ? `${numero} ${unidad}` : numero;
}

/**
 * El catálogo con lo vendido ya restado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ SE RESTA AQUÍ Y NO SE VUELVE A PEDIR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo primero que va a hacer cualquiera es vender tres refrescos y mirar la
 * esquina. Si sigue diciendo 12, el número no vale nada — y volver a bajar el
 * catálogo entero después de cada venta son 18 páginas por cobrar un chicle.
 *
 * Restando aquí, la esquina baja al instante, con internet o sin él. El número
 * del servidor vuelve a mandar la próxima vez que se abre Vender.
 *
 * Devuelve también los que cambiaron, para guardar solo esos en el aparato.
 */
export function restarLoVendido(
  items: readonly ItemListRow[],
  lineas: readonly { itemId: string; cantidad: number }[],
): { items: ItemListRow[]; cambiados: ItemListRow[] } {
  const vendido = new Map<string, number>();
  for (const l of lineas) vendido.set(l.itemId, (vendido.get(l.itemId) ?? 0) + l.cantidad);

  const cambiados: ItemListRow[] = [];
  const nuevos = items.map((item) => {
    const cuanto = vendido.get(item.id);
    // Sin número no hay nada que restar: un combo no tiene existencia propia
    // (salen sus piezas), y restarle a un vacío inventaría un «−2».
    if (cuanto === undefined || item.existencia == null) return item;
    // ⚠️ Redondeado a los tres decimales de la base: 1,1 − 0,2 en coma
    // flotante da 0,9000000000000001, y la esquina diría eso.
    // Lo que pierde de peso sale también —el queso que se seca—, igual que en
    // el servidor. Ver `lib/merma.ts`.
    const conSuMerma = conMerma(cuanto, item.merma, item.unit);
    const queda = Math.round((Number(item.existencia) - conSuMerma) * 1000) / 1000;
    const nuevo = { ...item, existencia: String(queda) };
    cambiados.push(nuevo);
    return nuevo;
  });

  return { items: cambiados.length > 0 ? nuevos : (items as ItemListRow[]), cambiados };
}
